import React, { useEffect, useState } from "react";
import { useRouteError } from "react-router-dom";

const CHUNK_RELOAD_KEY = "chunkReloadPending";

// Records an automatic reload unless one is already pending. When the marker
// can't be stored, nothing would stop a reload loop, so don't reload at all.
function markChunkReload() {
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

// Called when a rendered route's chunk loads, re-arming the automatic reload.
export function clearChunkReload(module) {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {}
  return module;
}

// Imported eagerly: it has to render when a lazy route's chunk can't load.
export default function RouteError() {
  const error = useRouteError();
  const isChunkError = error?.name === "ChunkLoadError";
  const [shouldReload, setShouldReload] = useState(isChunkError);

  // A missing chunk usually means a deploy replaced the hashed files this tab
  // was built against, and React.lazy caches the failed import. Reloading
  // fetches the new index.html; if the chunk still fails after that, show the
  // manual recovery screen instead of looping.
  useEffect(() => {
    if (!isChunkError) return;
    if (markChunkReload()) window.location.reload();
    else setShouldReload(false);
  }, [isChunkError]);

  if (shouldReload) {
    return (
      <div className="auth-loading">
        <div className="auth-spinner" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Something went wrong</h2>
      <p style={styles.description}>This page failed to load.</p>
      <button onClick={() => window.location.reload()}>Reload</button>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "960px",
    margin: "0 auto",
    padding: "80px 24px",
    textAlign: "center",
  },
  heading: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "var(--color-text-primary)",
  },
  description: {
    color: "var(--color-text-secondary)",
    marginBottom: "20px",
    fontSize: "14px",
  },
};
